/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import { useRef, useState, useEffect } from 'react';
import { useEffectOnce } from '@hooks';
import {
  Button, Snackbar, IconButton, Stack,
} from '@mui/material';
import { isEmpty } from 'validator';
import CloseIcon from '@mui/icons-material/Close';
import { isPossiblePhoneNumber, AsYouType } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import request from '@api';
import { Input, Select } from '@components';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@hooks/useAuthContext';

import 'react-phone-input-2/lib/style.css';
import './global-phone-input.css';
import classes from './index.module.css';

function OnboardingSurvey() {
  const { updateUser } = useAuthContext();
  const navigate = useNavigate();
  const formRef = useRef();
  const sellOptions = useRef([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [disableButton, setDisableButton] = useState(true);
  const [firstCategoryOptions, setFirstCategoryOptions] = useState([]);
  const [secondCategoryOptions, setSecondCategoryOptions] = useState([]);
  const [hearAboutUsOptions, setHearAboutUsOptions] = useState([]);
  const [selectedFirstCategoryName, setSelectedFirstCategoryName] = useState('');

  // if (stage === 'questionnaire_completed') {
  //   return <Navigate to="/" state={{ from: location }} replace />;
  // }

  const {
    handleSubmit, register, control, setValue, reset, getValues, watch,
  } = useForm({
    defaultValues: {
      inputStoreName: '',
      primaryContactName: '',
      primaryPhoneNumber: '',
      firstCategory: '',
      secondCategory: '',
      howHearAboutUs: '',
    },
  });

  useEffect(() => {
    const options = [];
    // eslint-disable-next-line array-callback-return, consistent-return
    sellOptions.current.forEach((option) => {
      const { firstCategory, secondCategory, code } = option || {};
      if (firstCategory === selectedFirstCategoryName) {
        options.push({ name: secondCategory, value: code });
      }
    });
    setSecondCategoryOptions(options);
  }, [selectedFirstCategoryName]);

  useEffect(() => {
    const subscription = watch((data) => {
      let hasEmptyField = false;
      Object.keys(data).forEach((key) => {
        if (data[key] === '') {
          hasEmptyField = true;
        }
      });
      if (hasEmptyField !== disableButton) {
        setDisableButton(hasEmptyField);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, disableButton]);

  const onFirstSellOptionChange = () => {
    const index = getValues('firstCategory');
    const option = firstCategoryOptions[index];
    setSelectedFirstCategoryName(option.name);
    setValue('secondCategory', '');
  };

  const onSubmitError = (errors) => {
    const PHONE_INPUT = 'primaryPhoneNumber';
    Object.keys(errors).forEach((key) => {
      if (key === PHONE_INPUT) {
        document.querySelector(`#${PHONE_INPUT}`).focus();
        setSnackbar({ open: true, message: 'Invalid phone number' });
      }
    });
  };

  const onSubmitSuccess = async (data) => {
    const { secondCategory: sellingsCategoryCode, ...rest } = data;
    const res = await request('tell-us-more', {
      method: 'POST',
      body: { sellingsCategoryCode, ...rest },
    }).catch((err) => {
      console.log(err);
      setSnackbar({ open: true, message: 'Network Error !' });
    });
    const { code } = res;
    if (code === 0) {
      await updateUser().catch(console.log);
      navigate('/');
    } else {
      setSnackbar({ open: true, message: 'Network Error !' });
    }
  };

  useEffectOnce(() => {
    request('get-store-prefilled-value').then((res) => {
      const { code, data: formData } = res || {};
      if (code === 0) {
        const { primaryContactName, primaryPhoneNumber, inputStoreName } = formData || {};
        reset({ primaryPhoneNumber, primaryContactName, inputStoreName });
        setDisableButton(true);
      }
    }).catch(console.log);

    request('enums?enumName=primarilySell').then((res) => {
      const { data } = res;
      sellOptions.current = data;
      let firstOptions = data.map((option) => (option.firstCategory));
      firstOptions = Array.from(new Set(firstOptions));
      firstOptions = firstOptions.map((item, index) => ({ name: item, value: index }));
      setFirstCategoryOptions(firstOptions);
    }).catch(console.log);

    request('enums?enumName=hearAboutUs').then((res) => {
      let { data } = res;
      data = data.map((option) => ({ value: option.code, name: option.name }));
      setHearAboutUsOptions(data);
    }).catch(console.log);
  }, []);

  return (
    <div className={classes.content}>
      <div className={classes.circle_background} />
      <div className={classes.greeting}>
        <div className={classes.greeting_primary}>
          Welcome to Seel
        </div>
        <div className={classes.greeting_secondary}>
          We are glad you&#39;re here.
        </div>
      </div>
      <div className={classes.survey}>
        <div>
          <div className={classes.title}>Tell us more about your store</div>
        </div>
        <form
          onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
          className={classes.form}
          ref={formRef}
        >
          <Input name="Legal business name" label="Legal business name" {...register('inputStoreName', { required: true, validate: (val) => !isEmpty(val, { ignore_whitespace: true }) })} />

          <Input name="Primary contact name" label="Primary contact name" {...register('primaryContactName', { required: true, validate: (val) => !isEmpty(val, { ignore_whitespace: true }) })} />
          <div className={classes.section}>
            <div className={classes.section_label}>Primary phone number</div>
            <Controller
              name="primaryPhoneNumber"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  autoFormat={false}
                  onChange={(val) => {
                    field.onChange(new AsYouType().input(`+${val}`));
                  }}
                  country="us"
                  value={field.value}
                  specialLabel="primaryPhoneNumber"
                  inputProps={{ id: 'primaryPhoneNumber' }}
                />
              )}
              rules={{
                required: true,
                validate: (value) => (!!isPossiblePhoneNumber(value)),
              }}
            />
          </div>
          <div className={classes.section}>
            <div className={classes.section_label}>What does your store primarily sell?</div>
            <div className={classes.sell_selector}>
              <Controller
                render={({ field }) => (
                  <Select
                    style={{ width: 215 }}
                    field={field}
                    open
                    options={firstCategoryOptions}
                    onChange={(ev, data) => { field.onChange(ev, data); onFirstSellOptionChange(data); }}
                  />
                )}
                name="firstCategory"
                control={control}
                rules={{ required: true }}
              />
              <Controller
                render={({ field }) => (
                  <Select
                    style={{ width: 215 }}
                    field={field}
                    open
                    options={secondCategoryOptions}
                    onChange={(ev, data) => { field.onChange(ev, data); }}
                    disabled
                  />
                )}
                name="secondCategory"
                control={control}
                rules={{ required: true }}
              />
            </div>
          </div>
          <div className={classes.section}>
            <Controller
              render={({ field }) => (
                <Stack>
                  <div className={classes.section_label}>How did you hear about us?</div>
                  <Select
                    field={field}
                    open
                    options={hearAboutUsOptions}
                    onChange={field.onChange}
                  />
                </Stack>
              )}
              name="howHearAboutUs"
              control={control}
              rules={{ required: true }}
            />
          </div>
        </form>
        <Button
          style={{ textTransform: 'none' }}
          disabled={disableButton}
          sx={{
            width: '450px',
            height: '48PX',
            borderRadius: 0,
            boxSizing: 'border-box',
            backgroundColor: '#645AFF',
            color: 'white',
            '&:hover': {
              backgroundColor: '#5950DF',
            },
            '&:disabled': {
              backgroundColor: '#C8C5FF',
              color: '#FFFFFF',
            },
          }}
          onClick={() => {
            if (formRef.current) {
              formRef.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true }),
              );
            }
          }}
        >
          Start
        </Button>
        <Snackbar
          sx={{
            '& .MuiPaper-root.MuiSnackbarContent-root': {
              backgroundColor: '#D82C0D',
            },
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={!!((snackbar.open && snackbar.message))}
          autoHideDuration={10000}
          message={snackbar.message}
          onClose={() => setSnackbar({ open: false, message: '' })}
          action={(
            <IconButton
              aria-label="close"
              color="inherit"
              sx={{ p: 0.5 }}
              onClick={() => setSnackbar({ open: false, message: '' })}
            >
              <CloseIcon />
            </IconButton>
          )}
        />
      </div>
    </div>
  );
}

export default OnboardingSurvey;
